import * as React from 'react';
// Import material ui modules.
import {
	ArrowForward,
	ArrowBack,
	Check,
} from '@mui/icons-material';
// Import hooks.
import {
	useFormMortgageeBase,
	UseFormMortgageeBaseParameters,
	UseFormMortgageeBaseResults,
} from '@app/hooks/view';
// Import props.
import {
	FormMortgageeProps,
	FormProps,
	NavigationFooterProps,
	ScrollbarProps,
	StatusProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
} from '@app/enumerations';

type UseFormMortgageeParameters = UseFormMortgageeBaseParameters<FormMortgageeProps>;

interface UseFormMortgageeResults extends UseFormMortgageeBaseResults {
	applyFormProps: () => FormProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
}

const useFormMortgagee = (params: UseFormMortgageeParameters): UseFormMortgageeResults => {

	// #region exported methods

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Mortgagee',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: false,
			statusProps: applyStatusProps(),
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		let isSaveRequired: boolean = doesFormRequireSave();
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Back',
			leftButtonClick: params.props.onBack,
			leftButtonIcon: ArrowBack,
			rightButtonDisabled: false,
			rightButtonLabel: isSaveRequired ? 'Save' : 'Continue',
			rightButtonClick: isSaveRequired ? submitForm : params.props.onForward,
			rightButtonIcon: isSaveRequired ? Check : ArrowForward,
			alignment: NavigationFooterAlignment.SPACE_BETWEEN,
		};
	};

	// #endregion exported methods

	// #region private methods

	/**
	 * Returns props for the status. 
	 **/
	const applyStatusProps = (): StatusProps | undefined => {
		let statusProps: StatusProps | undefined = undefined;
		if (showStatusMessage) {
			statusProps = {
				statusMessage: params.props.statusMessage ?? String.empty(),
				statusType: AppThemeColor.ERROR,
			}
		}

		return statusProps;
	}

	/**
	 * Checks to see if the form requires saving. 
	 **/
	const doesFormRequireSave = (): boolean => {
		return allowEdits
			&& !formMortgageeBase.areAllInputsDisabled
			&& (doesFormHaveChanges()
				|| params.props.isReviewRequired);
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	//#endregion id generators

	// #endregion private methods


	// #region variables

	const allowEdits: boolean = React.useMemo(() => params.props.allowEdits ?? true, [params.props.allowEdits])
	const [showStatusMessage, setShowStatusMessage] = React.useState<boolean>(false);

	const {
		submitForm,
		addressVerification,
		checkFormValidation,
		doesFormHaveChanges,
		...formMortgageeBase
	} = useFormMortgageeBase(params);

	// #endregion variables


	// #region life-cycle methods.

	// Called when updates are made the props related to the status of the form.
	React.useEffect(() => {
		// Determine if the status message should be rendered.
		if (params.props.isReviewRequired
			&& !String.isNullOrWhiteSpace(params.props.statusMessage)
			&& allowEdits
			&& checkFormValidation(false)) {
			setShowStatusMessage((currentShowStatusMessage: boolean) => true);
		}
		else {
			setShowStatusMessage((currentShowStatusMessage: boolean) => false);
		}
	}, [params.props.isReviewRequired, params.props.statusMessage, allowEdits]);

	// #endregion life-cycle methods.

	return {
		applyFormProps,
		applyNavigationFooterProps,
		submitForm,
		addressVerification,
		checkFormValidation,
		doesFormHaveChanges,
		...formMortgageeBase,
	};
}

// Export parameters.
export {
	UseFormMortgageeParameters,
}

// Export results.
export {
	UseFormMortgageeResults,
}

export default useFormMortgagee;